var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
import React, { useEffect, useMemo, useState, useCallback, useImperativeHandle } from "react";
import { useTransform, useMotionTemplate, motion, animate, useMotionValue } from "framer-motion";
function ItemWrapper({
  children,
  childrenCount,
  index,
  margin,
  startIndex
}) {
  const pos = useTransform(startIndex, (value) => (value <= index ? -value : childrenCount - value) * 100);
  const transform = useMotionTemplate`translateX(${pos}%)`;
  const style = {
    boxSizing: "border-box",
    flex: 1,
    transform,
    willChange: "transform",
    paddingRight: margin
  };
  return /* @__PURE__ */ React.createElement(motion.div, {
    style
  }, children);
}
const Slider = React.forwardRef(function Slider2(_a, ref) {
  var _b = _a, { children, style = {}, margin, count, index } = _b, props = __objRest(_b, ["children", "style", "margin", "count", "index"]);
  const childrenCount = React.Children.count(children);
  const tail = childrenCount - count;
  const frameWidth = 100 / childrenCount;
  const startIndex = useTransform(index, (value) => {
    if (!tail) {
      return 0;
    }
    if (value >= 0) {
      return Math.floor(value / tail) * tail % childrenCount;
    }
    return (childrenCount + (Math.ceil(value / tail) * tail - tail) % childrenCount) % childrenCount;
  });
  const translate = useTransform(index, (value) => {
    if (!tail) {
      return 0;
    }
    if (value >= 0) {
      return frameWidth * (value % tail);
    }
    return frameWidth * (tail + value % tail);
  });
  const containerStyle = __spreadProps(__spreadValues({}, style), {
    overflow: "hidden",
    touchAction: "pan-y"
  });
  const transform = useMotionTemplate`translateX(-${translate}%)`;
  const sliderStyle = {
    height: "100%",
    transform,
    willChange: "transform",
    display: "flex",
    width: `calc(((100% - ${(count - 1) * margin}px) / ${count} + ${margin}px)*${childrenCount})`
  };
  return /* @__PURE__ */ React.createElement("div", __spreadValues({
    ref,
    style: containerStyle
  }, props), /* @__PURE__ */ React.createElement(motion.div, {
    style: sliderStyle
  }, React.Children.map(children, (child, i) => /* @__PURE__ */ React.createElement(ItemWrapper, {
    childrenCount,
    index: i,
    margin,
    startIndex
  }, child))));
});
function animateSpring(index, to) {
  animate(index, to, {
    bounce: 0,
    type: "spring",
    velocity: 0
  });
}
function useAutoplay(index, interval) {
  const timer = React.useRef(0);
  const stop = React.useCallback(() => {
    if (!timer.current) {
      return;
    }
    window.clearInterval(timer.current);
    timer.current = 0;
  }, [timer]);
  const start = React.useCallback(() => {
    stop();
    if (!interval) {
      return;
    }
    timer.current = window.setInterval(() => {
      animateSpring(index, Math.floor(index.get() + 1));
    }, interval);
  }, [index, interval, timer, stop]);
  React.useEffect(() => {
    start();
    return () => {
      stop();
    };
  }, [start, stop]);
  return { start, stop };
}
function useOnChange({ childrenCount, index, onChange }) {
  useEffect(() => {
    let prevIndex = 0;
    const unsubscribe = index.onChange((value) => {
      if (!onChange) {
        return;
      }
      const newIndex = (Math.round(value) % childrenCount + childrenCount) % childrenCount;
      if (newIndex === prevIndex) {
        return;
      }
      prevIndex = newIndex;
      onChange(newIndex);
    });
    return () => {
      unsubscribe();
    };
  }, [index, childrenCount, onChange]);
}
function calcItemWidth(ref, count, margin) {
  if (!ref.current) {
    return 0;
  }
  const { width } = ref.current.getBoundingClientRect();
  return (width - margin * (count - 1)) / count + margin;
}
function usePan({ count, index, margin, ref }) {
  const [initial] = useState(() => ({
    dragging: false,
    index: index.get(),
    itemWidth: calcItemWidth(ref, count, margin)
  }));
  const onTouchMove = useCallback((event) => {
    event.preventDefault();
  }, []);
  const onPanStart = useCallback(() => {
    index.stop();
    initial.dragging = true;
    initial.index = index.get();
    initial.itemWidth = calcItemWidth(ref, count, margin);
    document.documentElement.addEventListener("touchmove", onTouchMove, { passive: false });
  }, [ref, count, index, initial, margin, onTouchMove]);
  const onPan = useCallback((_, info) => {
    const newIndex = initial.index - info.offset.x / initial.itemWidth;
    index.set(newIndex);
  }, [index, initial]);
  const onPanEnd = useCallback((event, info) => {
    var _a;
    initial.dragging = false;
    if (event instanceof MouseEvent) {
      (_a = event.target) == null ? void 0 : _a.addEventListener("click", (e) => {
        e.preventDefault();
      }, { once: true });
    }
    let newIndex;
    if (info.velocity.x > 100) {
      newIndex = Math.floor(index.get());
    } else if (info.velocity.x < -100) {
      newIndex = Math.ceil(index.get());
    } else {
      newIndex = Math.round(index.get());
    }
    animateSpring(index, newIndex);
    document.documentElement.removeEventListener("touchmove", onTouchMove);
  }, [index, initial, onTouchMove]);
  return useMemo(() => ({
    onPanStart,
    onPan,
    onPanEnd
  }), [onPanStart, onPan, onPanEnd]);
}
const MotionSlider = motion(Slider);
const PietileCarousel = React.forwardRef((_c, ref) => {
  var _d = _c, { autoplayInterval = 0, count = 1, children, draggable = true, margin = 0, onChange } = _d, props = __objRest(_d, ["autoplayInterval", "count", "children", "draggable", "margin", "onChange"]);
  const sliderRef = React.useRef(null);
  const index = useMotionValue(0);
  useOnChange({
    childrenCount: React.Children.count(children),
    index,
    onChange
  });
  const autoplay = useAutoplay(index, autoplayInterval);
  useImperativeHandle(ref, () => ({
    slideNext: () => {
      autoplay.start();
      const roundIndex = Number(index.get().toFixed(4));
      animateSpring(index, Math.ceil(roundIndex + 1));
    },
    slidePrev: () => {
      autoplay.start();
      const roundIndex = Number(index.get().toFixed(4));
      animateSpring(index, Math.floor(roundIndex - 1));
    },
    slideTo: (newIndex) => {
      autoplay.start();
      animateSpring(index, newIndex);
    }
  }), [autoplay, index]);
  const panHandlers = usePan({
    count,
    index,
    margin,
    ref: sliderRef
  });
  const onPanStart = useCallback((...args) => {
    autoplay.stop();
    panHandlers.onPanStart(...args);
  }, [autoplay, panHandlers]);
  const onPanEnd = useCallback((...args) => {
    autoplay.start();
    panHandlers.onPanEnd(...args);
  }, [autoplay, panHandlers]);
  let panProps = {};
  if (draggable) {
    panProps = {
      onPanStart,
      onPan: panHandlers.onPan,
      onPanEnd
    };
  }
  return /* @__PURE__ */ React.createElement(MotionSlider, __spreadValues(__spreadValues({
    ref: sliderRef,
    index,
    count,
    margin
  }, panProps), props), children);
});
export { PietileCarousel };
//# sourceMappingURL=pietile-carousel.es.js.map
